import { Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { AuthService } from '../../services/auth/auth.service';
import { environment } from '../../../environments/environment';

/**
 * Catch-all 404 page wired to the global `**` route. Visually mirrors
 * `ErrorPageComponent` so the two system pages feel like one family.
 *
 * Smart primary action — picks the destination from the live session
 * signals so a dev who mistypes a URL lands back on the dev landing,
 * a customer lands on the dashboard, and an unauthenticated visitor
 * is nudged straight to the login form (the "Go Back" escape hatch is
 * hidden in that case, since there is nowhere useful to go back to
 * pre-login). Falls back to `/` (which redirects to /mbrs/dashboard and
 * then defers to the auth guards) if signals are indeterminate or the
 * dev slug is empty.
 */
@Component({
  selector: 'app-not-found',
  standalone: true,
  imports: [CommonModule, ButtonModule, CardModule],
  template: `
    <div class="bg-[#F4F7FC] flex flex-col min-h-screen">
      <div class="flex flex-grow justify-center items-center">
        <p-card styleClass="!border !border-[#dde1e6] !shadow-none !p-0">
          <div class="w-[350px] xlplus:w-[400px] h-auto p-6">
            <div class="flex justify-center mb-6">
              <i class="pi pi-compass text-[4rem] text-taxpod-blue"></i>
            </div>

            <h1 class="font-lato font-bold text-xl xlplus:text-2xl text-center text-[#1e293b] mb-3">
              404 — Page Not Found
            </h1>
            <p
              class="font-lato font-normal text-sm xlplus:text-base text-center text-[#64748b] mb-6 leading-relaxed"
            >
              The page you're looking for doesn't exist or has been moved.
            </p>

            <div class="flex flex-col gap-3 min-h-[104px]">
              @if (authResolved()) {
                <p-button
                  [label]="primaryLabel()"
                  [icon]="primaryIcon()"
                  (onClick)="goHome()"
                  styleClass="!w-full !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold !text-sm xlplus:!text-base"
                />
                @if (showBack()) {
                  <p-button
                    label="Go Back"
                    icon="pi pi-arrow-left"
                    variant="outlined"
                    (onClick)="goBack()"
                    styleClass="!w-full !border-taxpod-blue !text-taxpod-blue !border-[2px] !font-lato !font-bold !text-sm xlplus:!text-base hover:!bg-[#E0F2FE]"
                  />
                }
              } @else {
                <div class="flex justify-center items-center h-[104px]" aria-hidden="true">
                  <i class="pi pi-spin pi-spinner text-2xl text-taxpod-blue"></i>
                </div>
              }
            </div>
          </div>
        </p-card>
      </div>
      <footer
        class="h-[62px] p-6 flex justify-center items-center text-[12px] xlplus:text-[12px] text-gray-400 font-lato"
      >
        Copyright © {{ currentYear }} YYC Holdings Sdn. Bhd.
      </footer>
    </div>
  `,
  styles: [],
})
export class NotFoundComponent implements OnInit {
  private router = inject(Router);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  currentYear: number = new Date().getFullYear();

  ngOnInit(): void {
    // The 404 route sits outside every auth guard, so a visitor who lands
    // here cold will have `isAuthenticated() === null` (indeterminate) and
    // our adaptive primary button cannot decide between "Go to Home" and
    // "Go to Login Page". Kick a `/auth/is-authenticated` probe so the
    // signal resolves and the template reactively settles on the right
    // CTA. Cheap cookie-auth GET; harmless when the session is already
    // resolved (we just skip the call).
    if (this.auth.isAuthenticated() === null) {
      this.auth
        .ensureUserAuthenticated()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  // `null` means the auth probe is still in flight. Gate the whole CTA
  // block on a resolved state so the user never sees "Go to Home" flash
  // before it swaps to "Go to Login Page" once the server answers.
  readonly authResolved = computed(() => this.auth.isAuthenticated() !== null);
  private readonly isLoggedOut = computed(() => this.auth.isAuthenticated() === false);

  readonly primaryLabel = computed(() => (this.isLoggedOut() ? 'Go to Login Page' : 'Go to Home'));
  readonly primaryIcon = computed(() => (this.isLoggedOut() ? 'pi pi-sign-in' : 'pi pi-home'));
  readonly showBack = computed(() => !this.isLoggedOut());

  goHome(): void {
    if (this.auth.isDev() === true) {
      const slug = environment.devAreaSlug;
      if (slug) {
        this.router.navigate([`/dev-${slug}`, 'logs', 'api-audit']);
        return;
      }
    }
    if (this.auth.isAuthenticated() === true) {
      this.router.navigateByUrl(this.auth.homeUrl());
      return;
    }
    if (this.auth.isAuthenticated() === false) {
      this.router.navigate(['/auth/login']);
      return;
    }
    // Indeterminate auth state — defer to root, which redirects through
    // the auth guards and lands the user wherever they belong.
    this.router.navigate(['/']);
  }

  goBack(): void {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.goHome();
    }
  }
}
